import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { globalConfig } from './configuration/config';
import { authStore } from './core/stores/auth-store';
import { openModal } from './core/modal/open-modal';
import { InsufficientResourceDialog } from './components/dialogs/insufficient-resource-dialog';

export class Api {
  private axios: AxiosInstance;
  private getAccessToken: (() => Promise<string | undefined>) | null = null;

  init() {
    this.axios = axios.create({
      baseURL: `${globalConfig.config.baseApiUrl}/v1`,

    });

    this.axios.interceptors.request.use(async (config) => {
      // If we have a token getter, use it to get fresh token
      if (this.getAccessToken) {
        try {
          const token = await this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            return Promise.reject(new Error('No access token available'));
          }
        } catch (e) {
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error?.response && error.response.status === 507) {
          openModal(InsufficientResourceDialog, { reason: 'storage-limit' });

        }

        // Handle 401/403 errors - token may have expired
        if (error?.response && (error.response.status === 401 || error.response.status === 403)) {
          // If we have a token getter, try to refresh and retry
          if (this.getAccessToken && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const token = await this.getAccessToken();
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.axios(originalRequest);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
            }
          }

          // If refresh failed or no token getter, mark as unauthorized
          if (error.response.status === 401) {
            authStore.setAuthorized(false);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Set the token getter function for automatic token refresh.
   * This should be called when WorkOS auth is initialized.
   */
  setTokenGetter(getter: () => Promise<string | undefined>) {
    this.getAccessToken = getter;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.get(url, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T> | any> {
    const request = () =>
      this.axios.post(url, data, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  updateTokenInHeaders(token: string) {
    this.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.delete(url, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.put(url, data, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.patch(url, data, {
        ...config,
      });
    return await request();
  }

  private async handleError(error: AxiosError): Promise<any> {
    if (!error.response) {
      throw error;
    }

    throw error;
  }
}

const api = new Api();

export { api };
